import { AgGridCustomFilterAdapter } from '@components/CustomTableFilter';
import type { CatalogUerBaseCostData } from '@gql/graphql';
import { RatioTypeEnum, SortEnumType } from '@gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

import {
    catalogUERBaseCostGroupNameFilter,
    catalogUERBaseCostNameFilter,
    catalogUERBaseCostSectionNameFilter,
} from '../../../CatalogUERBaseCost/catalogUerBaseCostQueries';
import { BaseCostsTableCellRenderCheckbox } from '../components/BaseCostsTableCellRenderCheckbox';
import { defaultColDefSet } from '../constants';
import { massApplicationBaseCostsCodeFilterGql } from '../massRatioApplicationQueries';
import type { TTableFiltersProps } from '../types';

type TUseMassApplicationBaseCostsColumnsProps = {
    catalogUniqueId?: string;
    checkedIds: Record<string, CatalogUerBaseCostData | null>;
    handleChangeCheckbox: (row: CatalogUerBaseCostData) => ({ checked }: { checked: boolean }) => void;
    changeFilterHandler: (filter: TTableFiltersProps) => void;
    canCheckRow?: boolean;
    canCheckGroupRow?: boolean;
    currentRowData: CatalogUerBaseCostData[] | null;
    ratioType?: RatioTypeEnum;
};

export const useMassApplicationBaseCostsColumns = ({
    catalogUniqueId,
    changeFilterHandler,
    canCheckRow,
    canCheckGroupRow,
    checkedIds,
    handleChangeCheckbox,
    currentRowData,
    ratioType,
}: TUseMassApplicationBaseCostsColumnsProps) => {
    const isRatioGroupIdsDataNeeded = useMemo(() => ratioType !== RatioTypeEnum.TechPartCoef, [ratioType]);

    const variables = useMemo(
        () => ({
            catalogUniqueId,
        }),
        [catalogUniqueId],
    );

    const columns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 46,
                resizable: false,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                cellRenderer: BaseCostsTableCellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox,
                    currentRowData,
                    canCheckRow,
                    canCheckGroupRow,
                },
            },
            {
                headerName: 'Раздел',
                field: 'sectionName',
                initialWidth: isRatioGroupIdsDataNeeded ? 400 : 200,
                spanHeaderHeight: true,
                ...defaultColDefSet,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogUERBaseCostSectionNameFilter',
                    customRequestId: 'catalogUERBaseCostSectionNameFilter',
                    query: catalogUERBaseCostSectionNameFilter,
                    variables: {
                        ...variables,
                        input: {},
                        order: [
                            {
                                value: SortEnumType.Asc,
                            },
                        ],
                        take: 1000,
                    },
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Подраздел',
                field: 'groupName',
                initialWidth: 200,
                spanHeaderHeight: true,
                ...defaultColDefSet,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogUERBaseCostGroupNameFilter',
                    customRequestId: 'catalogUERBaseCostGroupNameFilter',
                    query: catalogUERBaseCostGroupNameFilter,
                    variables: {
                        ...variables,
                        input: {},
                        order: [
                            {
                                value: SortEnumType.Asc,
                            },
                        ],
                        take: 1000,
                    },
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                initialWidth: 200,
                spanHeaderHeight: true,
                ...defaultColDefSet,
                filter: AgGridCustomFilterAdapter,
                initialSort: 'asc',
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogUERBaseCostItems',
                    customRequestId: 'massApplicationBaseCostsCodeFilter',
                    query: massApplicationBaseCostsCodeFilterGql,
                    variables: {
                        ...variables,
                        order: [
                            {
                                code: SortEnumType.Asc,
                            },
                        ],
                        take: 1000,
                    },
                    whereVariables: ['code'],
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                initialWidth: 475,
                minWidth: 300,
                spanHeaderHeight: true,
                ...defaultColDefSet,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogUERBaseCostNameFilter',
                    customRequestId: 'catalogUERBaseCostNameFilter',
                    query: catalogUERBaseCostNameFilter,
                    variables: {
                        ...variables,
                        input: {},
                        order: [
                            {
                                value: SortEnumType.Asc,
                            },
                        ],
                        take: 1000,
                    },
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед.изм.',
                field: 'unit',
                minWidth: 88,
                initialWidth: 100,
                spanHeaderHeight: true,
            },
        ],
        [
            canCheckGroupRow,
            canCheckRow,
            changeFilterHandler,
            checkedIds,
            currentRowData,
            handleChangeCheckbox,
            isRatioGroupIdsDataNeeded,
            variables,
        ],
    );

    return {
        columns,
    };
};
